<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <header class="bg-white shadow-sm border-b">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center py-4">
          <div>
            <h1 class="text-2xl font-bold text-gray-900 font-inter">reepo.io</h1>
            <p class="text-gray-600">{{ client?.company_name }}</p>
          </div>
          <button @click="handleLogout" class="btn-tertiary">
            Sign Out
          </button>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div class="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 p-6 border border-gray-100">
          <div class="text-xl font-bold text-gray-900">{{ stats.totalLeads }}</div>
          <div class="text-gray-600">Total Leads</div>
        </div>
        <div class="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 p-6 border border-gray-100">
          <div class="text-xl font-bold text-gray-900">{{ stats.recentLeads }}</div>
          <div class="text-gray-600">Recent Leads (7 days)</div>
        </div>
        <div class="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 p-6 border border-gray-100">
          <div class="text-xl font-bold text-gray-900">{{ stats.activeShows }}</div>
          <div class="text-gray-600">Active Shows</div>
        </div>
      </div>

      <!-- Quick Actions as link-cards (muted) -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <button @click="navigateTo('/admin/leads')" class="bg-white rounded-lg shadow-sm ring-1 ring-gray-200 hover:shadow-md hover:ring-gray-300 transition-all duration-200 p-6 text-left">
          <div class="flex items-center">
            <div class="flex-shrink-0 text-gray-400 group-hover:text-gray-600">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
              </svg>
            </div>
            <div class="ml-4">
              <h3 class="text-lg font-medium text-gray-900">Manage Leads</h3>
              <p class="text-sm text-gray-600">View, search, and export</p>
            </div>
          </div>
        </button>
        <button @click="navigateTo('/admin/shows')" class="bg-white rounded-lg shadow-sm ring-1 ring-gray-200 hover:shadow-md hover:ring-gray-300 transition-all duration-200 p-6 text-left">
          <div class="flex items-center">
            <div class="flex-shrink-0 text-gray-400 group-hover:text-gray-600">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
              </svg>
            </div>
            <div class="ml-4">
              <h3 class="text-lg font-medium text-gray-900">Manage Shows</h3>
              <p class="text-sm text-gray-600">Create and manage</p>
            </div>
          </div>
        </button>
        <button @click="navigateTo('/admin/settings')" class="bg-white rounded-lg shadow-sm ring-1 ring-gray-200 hover:shadow-md hover:ring-gray-300 transition-all duration-200 p-6 text-left">
          <div class="flex items-center">
            <div class="flex-shrink-0 text-gray-400 group-hover:text-gray-600">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
              </svg>
            </div>
            <div class="ml-4">
              <h3 class="text-lg font-medium text-gray-900">Account Settings</h3>
              <p class="text-sm text-gray-600">Manage your account</p>
            </div>
          </div>
        </button>
        <button @click="exportAllLeads" class="bg-white rounded-lg shadow-sm ring-1 ring-gray-200 hover:shadow-md hover:ring-gray-300 transition-all duration-200 p-6 text-left">
          <div class="flex items-center">
            <div class="flex-shrink-0 text-gray-400 group-hover:text-gray-600">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
            </div>
            <div class="ml-4">
              <h3 class="text-lg font-medium text-gray-900">Export All Leads</h3>
              <p class="text-sm text-gray-600">Download CSV</p>
            </div>
          </div>
        </button>
      </div>

      <!-- Shows Section -->
      <div class="bg-white rounded-lg shadow mb-8">
        <div class="px-6 py-4 border-b border-gray-200">
          <div class="flex justify-between items-center mb-6">
            <h2 class="text-lg font-medium text-gray-900">Your Shows</h2>
            <button @click="navigateTo('/admin/shows')" class="text-sm text-gray-600 hover:text-gray-800">
              View all shows →
            </button>
          </div>
        </div>
        <div class="p-6">
          <div v-if="loading" class="text-center py-8">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p class="mt-2 text-gray-600">Loading shows...</p>
          </div>
          <div v-else-if="shows.length === 0" class="text-center py-8">
            <p class="text-gray-600">No shows found.</p>
            <button
              @click="navigateTo('/admin/shows')"
              class="mt-4 btn-primary"
            >
              Create Your First Show
            </button>
          </div>
          <div v-else class="space-y-4">
            <div
              v-for="show in shows"
              :key="show.id"
              @click="navigateTo(`/admin/leads?show_id=${show.id}`)"
              class="interactive-card p-6"
            >
              <div class="flex justify-between items-start">
                <div>
                  <h3 class="text-lg font-medium text-gray-900">{{ show.name }}</h3>
                  <p class="text-gray-600">
                    {{ formatDate(show.start_date) }} - {{ formatDate(show.end_date) }}
                  </p>
                  <p class="text-sm text-gray-500 flex items-center gap-2 flex-wrap">
                    <span>{{ show.lead_count }} total</span>
                    <span>·</span>
                    <StatusBadge :status="show.status" />
                    <span v-if="show.final_price" class="text-gray-700 font-medium">
                      €{{ show.final_price }}
                    </span>
                    <StatusBadge v-if="show.status === 'approved' && show.payment_status === 'pending'" status="payment_pending" />
                    <StatusBadge v-if="show.status === 'approved' && show.payment_status === 'paid'" status="paid" />
                  </p>
                </div>
                <div class="text-[13px] text-gray-500">Open →</div>
              </div>
            </div>
          </div>
        </div>
      </div>



      <!-- Recent Leads Section -->
      <div v-if="recentLeads.length > 0" class="bg-white rounded-lg shadow">
        <div class="px-6 py-4 border-b border-gray-200">
          <h2 class="text-lg font-medium text-gray-900">Recent Leads</h2>
        </div>
        <div class="p-6">
          <div class="space-y-4">
            <div
              v-for="lead in recentLeads"
              :key="lead.id"
              class="border border-gray-200 rounded-lg p-4"
            >
              <div class="flex justify-between items-start">
                <div>
                  <h3 class="font-medium text-gray-900">{{ lead.name }}</h3>
                  <p class="text-gray-600">{{ lead.email }}</p>
                  <p v-if="lead.company" class="text-gray-600">{{ lead.company }}</p>
                  <p v-if="lead.interest" class="text-sm text-gray-500">
                    Interest: {{ lead.interest }}
                  </p>
                </div>
                <div class="text-sm text-gray-500">
                  {{ formatDateTime(lead.created_at) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/authStore.js'
import apiClient from '../utils/api.js'
import StatusBadge from '../components/StatusBadge.vue'

// Router
const router = useRouter()

// Reactive data
const loading = ref(true)
const client = ref(null)
const shows = ref([])
const recentLeads = ref([])
const stats = reactive({
  totalLeads: 0,
  recentLeads: 0,
  activeShows: 0
})



// Methods
const loadDashboard = async () => {
  try {
    // Use auth store to check authentication
    const authStore = useAuthStore()
    if (!authStore.isAuthenticated) {
      router.push('/admin/login')
      return
    }

    // Use API client for consistent error handling
    const data = await apiClient.get('/admin/dashboard')
    
    console.log('Dashboard data:', data)
    console.log('Client from dashboard:', data.client)
    
    client.value = data.client
    shows.value = data.shows
    stats.totalLeads = data.total_leads
    stats.recentLeads = data.recent_leads
    stats.activeShows = data.shows.filter(show => show.status === 'active' || show.status === 'approved').length
    
    // Get recent leads from first show
    if (data.shows.length > 0) {
      await loadRecentLeads(data.shows[0].id)
    }
    
  } catch (error) {
    console.error('Error loading dashboard:', error)
    // API client handles 401s automatically - just show error
    if (error.message.includes('Authentication failed')) {
      // User will be redirected to login by API client
      return
    }
  } finally {
    loading.value = false
  }
}

const loadRecentLeads = async (showId) => {
  try {
    const data = await apiClient.get(`/admin/leads?show_id=${showId}&per_page=5`)
    recentLeads.value = data.leads
  } catch (error) {
    console.error('Error loading recent leads:', error)
  }
}

const navigateTo = (path) => {
  router.push(path)
}

const exportAllLeads = async () => {
  try {
    const blob = await apiClient.download('/admin/leads/export')
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `all_leads_export_${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  } catch (error) {
    console.error('Error exporting leads:', error)
  }
}

const exportShowLeads = async (show) => {
  try {
    const blob = await apiClient.download(`/admin/leads/export?show_id=${show.id}`)
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `leads_${show.name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  } catch (error) {
    console.error('Error exporting show leads:', error)
  }
}

const handleLogout = async () => {
  const authStore = useAuthStore()
  await authStore.logout()
}

import { formatDate as fmtDate, formatDateTime as fmtDateTime } from '../utils/date.js'

const formatDate = (dateString) => fmtDate(dateString)
const formatDateTime = (dateString) => fmtDateTime(dateString)

// Lifecycle
onMounted(() => {
  loadDashboard()
})
</script> 